import React, { useState, useMemo } from 'react';
import { Spinner } from './ui';
import { CheckCircle } from 'lucide-react';

const IframeAnalyzer = ({ url, profileProgress, className = '' }) => {
  const [iframeLoaded, setIframeLoaded] = useState(false);
  const stepDescription = useMemo(() => {
    switch (profileProgress) {
      case 35:
        return 'Starting profile creation...';
      case 70:
        return 'Analyzing your website...';
      case 100:
        return 'Profile created successfully!';
      default:
        return '';
    }
  }, [profileProgress]);
  return (
    <div className={`relative overflow-hidden rounded-md border ${className}`}>
      {/* Loading Placeholder */}
      {!iframeLoaded && (
        <div className='absolute inset-0 flex flex-col items-center justify-center bg-gradient-to-br from-gray-50 to-gray-100 z-10'>
          <div className='text-center space-y-4'>
            <Spinner size='lg' className='mx-auto' />
            <div className='space-y-2'>
              <p className='text-gray-700 font-medium text-lg'>Loading website preview...</p>
              <p className='text-gray-500 text-sm'>{url}</p>
            </div>
            {/* Animated dots */}
            <div className='flex items-center justify-center space-x-2'>
              <div
                className='w-2 h-2 bg-primary-500 rounded-full animate-bounce'
                style={{ animationDelay: '0s' }}
              />
              <div
                className='w-2 h-2 bg-primary-500 rounded-full animate-bounce'
                style={{ animationDelay: '0.2s' }}
              />
              <div
                className='w-2 h-2 bg-primary-500 rounded-full animate-bounce'
                style={{ animationDelay: '0.4s' }}
              />
            </div>
          </div>
        </div>
      )}

      {/* Iframe */}
      <iframe
        src={url}
        title='Website Preview'
        className={`w-full h-96 border-0 rounded-md transition-opacity duration-1000 pointer-events-none ${
          iframeLoaded ? 'opacity-100' : 'opacity-0'
        }`}
        sandbox='allow-same-origin'
        referrerPolicy='no-referrer'
        loading='lazy'
        onLoad={() => {
          setTimeout(() => {
            setIframeLoaded(true);
          }, 500);
        }}
      />

      {/* Analyzing Animation Overlay - Only show when iframe is loaded and not completed */}
      {iframeLoaded && (
        <div className='absolute inset-0 pointer-events-none'>
          {/* Scanning line effect */}
          {/* <div
            className='absolute w-full h-1 bg-gradient-to-r from-transparent via-primary-500 to-transparent opacity-70'
            style={{
              animation: 'scan 3s ease-in-out infinite',
              boxShadow: '0 0 20px rgba(59, 130, 246, 0.8)',
            }}
          /> */}

          {/* Corner brackets */}
          <div className='absolute top-4 left-4 w-12 h-12 border-l-2 border-t-2 border-primary-500 animate-pulse' />
          <div className='absolute top-4 right-4 w-12 h-12 border-r-2 border-t-2 border-primary-500 animate-pulse' />
          <div className='absolute bottom-4 left-4 w-12 h-12 border-l-2 border-b-2 border-primary-500 animate-pulse' />
          <div className='absolute bottom-4 right-4 w-12 h-12 border-r-2 border-b-2 border-primary-500 animate-pulse' />

          {/* Analyzing text overlay */}
          <div className='absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 bg-black/50 backdrop-blur-sm px-6 py-3 rounded-lg border border-primary-500/50'>
            <div className='flex items-center space-x-3'>
              {profileProgress === 100 ? (
                <div className='animate-check-draw-rotate'>
                  <CheckCircle className='w-8 h-8 text-green-400' />
                </div>
              ) : (
                <Spinner size='sm' className='text-primary-400' />
              )}
              <span className='text-white font-medium'>{stepDescription}</span>
            </div>
          </div>
        </div>
      )}

      {/* Cookie banner gradient overlays */}
      {/* <div className='absolute bottom-0 left-0 right-0 h-32 bg-gradient-to-t from-white to-transparent pointer-events-none z-10' />
      <div className='absolute top-0 left-0 right-0 h-24 bg-gradient-to-b from-white to-transparent pointer-events-none z-10' /> */}
    </div>
  );
};

export default IframeAnalyzer;
